import { create } from 'zustand';
import { Node, Edge } from 'reactflow';
import { devtools } from 'zustand/middleware';
import { produce } from 'immer';
import { supabase } from './supabase';

type NodeType = 'agent' | 'task' | 'tool' | 'flow';

interface ClearOptions {
  selectedOnly?: boolean;
  nodeTypes?: NodeType[];
}

interface ClearingState {
  inProgress: boolean;
  selectedOnly: boolean;
  nodeTypes: NodeType[];
}

interface EditorState {
  nodes: Node[];
  edges: Edge[];
  selectedNodeId: string | null;
  isDragging: boolean;
  history: Array<{ nodes: Node[]; edges: Edge[] }>;
  historyIndex: number;
  workflowId: string | null;
  projectId: string | null;
  version: number;
  isSaving: boolean;
  lastSavedAt: Date | null;
  autoSave: boolean;
  saveError: string | null;
  saveQueue: boolean;
  lastClearedAt: Date | null;
  clearingState: ClearingState;
  setNodes: (nodes: Node[] | ((prev: Node[]) => Node[])) => void;
  setEdges: (edges: Edge[] | ((prev: Edge[]) => Edge[])) => void;
  updateNodeData: (nodeId: string, data: any) => void;
  setSelectedNode: (nodeId: string | null) => void;
  setIsDragging: (isDragging: boolean) => void;
  pushToHistory: () => void;
  undo: () => void;
  redo: () => void;
  saveWorkflow: () => Promise<void>;
  loadWorkflow: (workflowId: string) => Promise<void>;
  createWorkflow: (projectId: string, name: string, description?: string) => Promise<string>;
  clearCanvas: (options?: ClearOptions) => void;
  startClearing: (options?: ClearOptions) => void;
  cancelClearing: () => void;
  setAutoSave: (enabled: boolean) => void;
}

export const useEditorStore = create<EditorState>()(
  devtools(
    (set, get) => ({
      nodes: [],
      edges: [],
      selectedNodeId: null,
      isDragging: false,
      history: [],
      historyIndex: -1,
      workflowId: null,
      projectId: null,
      version: 1,
      isSaving: false,
      lastSavedAt: null,
      autoSave: true,
      saveError: null,
      saveQueue: false,
      lastClearedAt: null,
      clearingState: {
        inProgress: false,
        selectedOnly: false,
        nodeTypes: [] as NodeType[],
      },

      setNodes: (nodes: Node[] | ((prev: Node[]) => Node[])) => {
        set(produce((state: EditorState) => {
          state.nodes = typeof nodes === 'function' ? nodes(state.nodes) : nodes;
        }));
      },

      setEdges: (edges: Edge[] | ((prev: Edge[]) => Edge[])) => {
        set(produce((state: EditorState) => {
          state.edges = typeof edges === 'function' ? edges(state.edges) : edges;
        }));
      },

      updateNodeData: (nodeId: string, data: Record<string, any>) => {
        set(produce((state: EditorState) => {
          const node = state.nodes.find((n: Node) => n.id === nodeId);
          if (node) {
            node.data = { ...node.data, ...data };
          }
        }));
      },

      setSelectedNode: (nodeId: string | null) => {
        set(produce((state: EditorState) => {
          state.selectedNodeId = nodeId;
          state.nodes = state.nodes.map((node: Node) => ({
            ...node,
            selected: node.id === nodeId,
            data: {
              ...node.data,
              selected: node.id === nodeId,
            },
          }));
        }));
      },

      setIsDragging: (isDragging: boolean) => {
        set({ isDragging });
      },

      pushToHistory: () => {
        const { nodes, edges, historyIndex, history } = get();
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push({
          nodes: JSON.parse(JSON.stringify(nodes)),
          edges: JSON.parse(JSON.stringify(edges)),
        });
        
        set({
          history: newHistory,
          historyIndex: historyIndex + 1,
        });
      },

      undo: () => {
        const { historyIndex, history } = get();
        if (historyIndex > 0) {
          const newIndex = historyIndex - 1;
          const { nodes, edges } = history[newIndex];
          set({
            nodes,
            edges,
            historyIndex: newIndex,
          });
        }
      },

      redo: () => {
        const { historyIndex, history } = get();
        if (historyIndex < history.length - 1) {
          const newIndex = historyIndex + 1;
          const { nodes, edges } = history[newIndex];
          set({
            nodes,
            edges,
            historyIndex: newIndex,
          });
        }
      },

      saveWorkflow: async () => {
        const state = get();
        const { nodes, edges, workflowId, projectId, version, isSaving, saveQueue } = state;
        
        if (!workflowId || !projectId) return;
        if (isSaving) {
          // If already saving, queue up another save
          if (!saveQueue) {
            set({ saveQueue: true });
          }
          return;
        }

        set({ isSaving: true, saveError: null });
        try {
          // First check if version matches to prevent conflicts
          const { data: current } = await supabase
            .from('workflows')
            .select('version')
            .eq('id', workflowId)
            .single();

          if (current && current.version !== version) {
            throw new Error('Version conflict: The workflow has been modified elsewhere');
          }

          const flowData = { nodes, edges };
          const { error } = await supabase
            .from('workflows')
            .update({ 
              flow_data: flowData,
              version: version + 1,
              updated_at: new Date().toISOString(),
              updated_by: (await supabase.auth.getUser()).data.user?.id
            })
            .eq('id', workflowId);

          if (error) throw error;

          set({ 
            version: version + 1,
            lastSavedAt: new Date(),
            isSaving: false,
            saveError: null
          });

          // If there was a queued save, execute it
          if (state.saveQueue) {
            set({ saveQueue: false });
            await get().saveWorkflow();
          }
        } catch (error: any) {
          console.error('Error saving workflow:', error);
          set({ 
            isSaving: false,
            saveError: error.message,
            saveQueue: false
          });
          throw error;
        }
      },

      loadWorkflow: async (workflowId: string) => {
        try {
          const { data, error } = await supabase
            .from('workflows')
            .select('*')
            .eq('id', workflowId)
            .single();

          if (error) throw error;
          if (!data) throw new Error('Workflow not found');

          const flowData = data.flow_data as { nodes: Node[]; edges: Edge[] };
          set({
            nodes: flowData.nodes,
            edges: flowData.edges,
            workflowId: data.id,
            projectId: data.project_id,
            version: data.version,
            history: [{ nodes: flowData.nodes, edges: flowData.edges }],
            historyIndex: 0,
            lastSavedAt: new Date(data.updated_at)
          });
        } catch (error) {
          console.error('Error loading workflow:', error);
          throw error;
        }
      },

      clearCanvas: (options?: { selectedOnly?: boolean; nodeTypes?: NodeType[] }) => {
        const { nodes, edges } = get();
        
        // Push current state to history before clearing
        get().pushToHistory();

        const nodesToKeep = nodes.filter((node: Node) => {
          if (options?.selectedOnly && !node.selected) return true;
          if (options?.nodeTypes?.length && !options.nodeTypes.includes(node.type as NodeType)) return true;
          return false;
        });

        const edgesToKeep = edges.filter((edge: Edge) => {
          const sourceNode = nodesToKeep.find((n: Node) => n.id === edge.source);
          const targetNode = nodesToKeep.find((n: Node) => n.id === edge.target);
          return sourceNode && targetNode;
        });

        set(produce((state: EditorState) => {
          state.nodes = nodesToKeep;
          state.edges = edgesToKeep;
          state.lastClearedAt = new Date();
          state.clearingState = {
            inProgress: false,
            selectedOnly: options?.selectedOnly || false,
            nodeTypes: (options?.nodeTypes || []) as NodeType[],
          };
        }));
      },

      startClearing: (options?: { selectedOnly?: boolean; nodeTypes?: NodeType[] }) => {
        set(produce((state: EditorState) => {
          state.clearingState = {
            inProgress: true,
            selectedOnly: options?.selectedOnly || false,
            nodeTypes: (options?.nodeTypes || []) as NodeType[],
          };
        }));
      },

      cancelClearing: () => {
        set(produce((state: EditorState) => {
          state.clearingState = {
            inProgress: false,
            selectedOnly: false,
            nodeTypes: [] as NodeType[],
          };
        }));
      },

      setAutoSave: (enabled: boolean) => {
        set({ autoSave: enabled });
      },

      createWorkflow: async (projectId: string, name: string, description?: string) => {
        const { nodes, edges } = get();
        const user = (await supabase.auth.getUser()).data.user;
        if (!user) throw new Error('Not authenticated');

        try {
          const { data, error } = await supabase
            .from('workflows')
            .insert({
              project_id: projectId,
              name,
              description,
              flow_data: { nodes, edges },
              created_by: user.id,
              updated_by: user.id
            })
            .select()
            .single();

          if (error) throw error;
          if (!data) throw new Error('Failed to create workflow');

          set({
            workflowId: data.id,
            projectId: data.project_id,
            version: 1,
            lastSavedAt: new Date()
          });

          return data.id;
        } catch (error) {
          console.error('Error creating workflow:', error);
          throw error;
        }
      }
    }),
    { name: 'editor-store' }
  )
);
